import React, { useRef, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import images from "../../../images";
import Button from "../../../components/common/Button/Button";
import CommonModal from "../../../components/common/Modal/ModalCommon";
import {
  useGetUserInfoQuery,
  usePostWithdrawMutation,
  useGetWithdrawDetailsQuery,
  useGetCalculateTokenQuery,
} from "../../../services/apiSlice";
import { useTranslation } from "react-i18next";
import LoadingCommon from "../../../components/common/LoadingStatus/LoadingCommon";
import NewHeader from "../../Home/NewHeader";
import CustomInputProps from "../../../components/CustomInputProps/CustomInputProps";
import UnderReview from "../../UnderReview/UnderReview";
import { usePostHog } from "../../../hooks/usePostHog";

const WithDraw = () => {
  const navigate = useNavigate();
  const modalRef = useRef();
  const { t } = useTranslation();
  const { trackEvent, trackTimer } = usePostHog();

  // const [modalContent, setModalContent] = useState(null);
  const [bankAccount, setBankAccount] = useState("");
  const [rawBankAccount, setRawBankAccount] = useState("");
  const [tempWithdrawAmount, setTempWithdrawAmount] = useState("");
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawId, setWithdrawId] = useState("");
  const debounceTimerRef = useRef(null);

  const { data: userInfo, isLoading, refetch } = useGetUserInfoQuery();
  const isAccountUnderReview = userInfo?.data?.status;

  const [postWithdraw, { isLoading: isWithdrawing }] =
    usePostWithdrawMutation();
  const { isLoadingWithdrawDetails } = useGetWithdrawDetailsQuery(
    { withdraw_id: withdrawId },
    { skip: !withdrawId }
  );

  const [error, setError] = useState({
    withdrawAmount: "",
    bankAccount: "",
  });

  const { data: calculateReceiveAmount } = useGetCalculateTokenQuery(
    { token_amount: withdrawAmount },
    { skip: !withdrawAmount }
  );

  const availableToken = userInfo?.data?.token || "0";
  
  useEffect(() => {
    refetch();
    
    // Track withdrawal page viewed
    trackEvent('withdrawal_page_viewed', {
      available_balance: availableToken,
      account_status: isAccountUnderReview,
      user_phone: userInfo?.data?.phone ? 'has_phone' : 'no_phone'
    });
  }, [refetch, trackEvent, availableToken, isAccountUnderReview, userInfo]);
  useEffect(() => {
    if (userInfo?.data?.phone) {
      const { phone } = userInfo.data;
      const formattedPhone =
        phone.length > 4 ? `${phone.slice(0, 4)}-${phone.slice(4, 8)}` : phone;
      setBankAccount(phone);
      setRawBankAccount(formattedPhone);

      setError((prev) => ({
        ...prev,
        bankAccount: "",
      }));
    }
  }, [userInfo]);

  const handleBankAccountChange = (value) => {
    if (value === "") {
      setBankAccount("");
      setRawBankAccount("");
      setError((prev) => ({
        ...prev,
        bankAccount: t("withDraw.bankAccountRequired"),
      }));
    } else {
      setError((prev) => ({
        ...prev,
        bankAccount: "",
      }));
      const cleanedValue = value.replace(/[^0-9]/g, "");
      setBankAccount(cleanedValue);
      const formattedPhone =
        cleanedValue.length > 4
          ? `${cleanedValue.slice(0, 4)}-${cleanedValue.slice(4, 8)}`
          : cleanedValue;
      setRawBankAccount(formattedPhone);
    }
  };

  const debouncedSetWithdrawAmount = useCallback((value) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setWithdrawAmount(value);
    }, 500); // Trì hoãn 500ms
  }, []);

  const handleWithdrawAmountChange = (value) => {
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setTempWithdrawAmount(value);

      if (Number(value) > Number(availableToken)) {
        setError((prev) => ({
          ...prev,
          withdrawAmount: t("withDraw.tokenExceedsBalance"),
        }));
      } else if (Number(value) <= 0 && value !== "") {
        setError((prev) => ({
          ...prev,
          withdrawAmount: t("withDraw.tokenLessThanZero"),
        }));
      } else if (Number(value) < 100) {
        setError((prev) => ({
          ...prev,
          withdrawAmount: t("withDraw.theMiniumAmount", {
            minimum: 100,
          }),
        }));
      } else {
        setError((prev) => ({
          ...prev,
          withdrawAmount: "",
        }));
      }
      debouncedSetWithdrawAmount(value);
    }
  };

  const openWithdrawModal = () => {
    if (
      (error.withdrawAmount !== "" && error.withdrawAmount !== undefined) ||
      (error.bankAccount !== "" && error.bankAccount !== undefined)
    ) {
      trackEvent('withdrawal_validation_failed', {
        amount: withdrawAmount,
        available_balance: availableToken,
        has_amount_error: error.withdrawAmount !== "",
        has_account_error: error.bankAccount !== "",
        conversion_rate: calculateReceiveAmount?.data?.transaction_fee || 0.5
      });
      return;
    }

    if (!bankAccount) {
      setError((prev) => ({
        ...prev,
        bankAccount: t("withDraw.bankAccountRequired"),
      }));
      trackEvent('withdrawal_validation_failed', {
        amount: withdrawAmount,
        error_type: 'missing_bank_account',
        available_balance: availableToken
      });
      return;
    } else if (!withdrawAmount) {
      setError((prev) => ({
        ...prev,
        withdrawAmount: t("withDraw.withdrawAmountRequired"),
      }));
      trackEvent('withdrawal_validation_failed', {
        bank_account: 'provided',
        error_type: 'missing_amount',
        available_balance: availableToken
      });
      return;
    } else {
      setError({
        withdrawAmount: "",
        bankAccount: "",
      });
      
      // Track withdrawal modal opened
      trackEvent('withdrawal_modal_opened', {
        amount: withdrawAmount,
        available_balance: availableToken,
        bank_account: bankAccount.slice(-4), // last 4 digits only
        conversion_rate: calculateReceiveAmount?.data?.transaction_fee || 0.5,
        receive_amount: calculateReceiveAmount?.data?.receive_amount || 0
      });
      
      modalRef.current?.openModal();
    }
  };
  const handleConfirmWithdraw = async () => {
    const withdrawalTimer = trackTimer('withdrawal_processing_duration');
    
    trackEvent('withdrawal_initiated', {
      amount: withdrawAmount,
      available_balance: availableToken,
      bank_account: bankAccount.slice(-4), // last 4 digits only
      conversion_rate: calculateReceiveAmount?.data?.transaction_fee || 0.5,
      receive_amount: calculateReceiveAmount?.data?.receive_amount || 0
    });

    try {
      const response = await postWithdraw({
        bank_account: bankAccount,
        token_amount: withdrawAmount,
      });
      
      setWithdrawId(response?.data?.data?.data.withdraw_id);
      
      if (response?.data?.status === 201) {
        withdrawalTimer.end({
          success: true,
          withdraw_id: response?.data?.data?.data.withdraw_id
        });

        trackEvent('withdrawal_success', {
          amount: withdrawAmount,
          withdraw_id: response?.data?.data?.data.withdraw_id,
          bank_account: bankAccount.slice(-4),
          remaining_balance: availableToken - withdrawAmount,
          receive_amount: calculateReceiveAmount?.data?.receive_amount || 0
        });

        await refetch();
        navigate("/profile/withdraw/success", {
          state: {
            withdrawId: response?.data?.data?.data.withdraw_id,
          },
        });
      }
    } catch (error) {
      withdrawalTimer.end({
        success: false,
        error_code: error.status,
        error_message: error.message
      });

      trackEvent('withdrawal_failed', {
        amount: withdrawAmount,
        bank_account: bankAccount.slice(-4),
        error_code: error.status,
        error_message: error.data?.detail?.[0]?.msg || error.message,
        available_balance: availableToken
      });

      // console.error("Withdrawal failed:", error);
      // Handle error
    }
  };

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        {t("base.loading")}
      </div>
    );
  }

  const topContent = (
    <div className="flex flex-col  gap-6">
      <div onClick={() => navigate("/profile")} className="cursor-pointer">
        <img
          src={images.ArrowLeftIconGray}
          alt="Withdraw"
          className="w-6 h-6"
        />
      </div>
      <h1 className="text-2xl font-bold text-[#253FF6]">
        {t("withDraw.withdraw")}
      </h1>
    </div>
  );

  return (
    <>
      {isAccountUnderReview === "under-reviewing" ? (
        <>
          <UnderReview />
        </>
      ) : (
        <>
          <LoadingCommon
            statusLoading={isLoadingWithdrawDetails || isWithdrawing}
          />
          <div className="flex flex-col w-full max-w-[480px] bg-white ">
            <div className=" fixed top-0 left-0 w-full z-50">
              <NewHeader />
            </div>
            <div className="flex flex-col flex-1 gap-3 py-3.5 px-[30px] mt-10">
              {topContent}
              <div className="pb-6 border-b border-[#E9E6E4]">
                <div className="flex justify-between bg-[#ECEEFF] rounded-md p-4">
                  <div className="flex flex-col gap-4">
                    <img
                      src={images.RallyCoins}
                      alt=""
                      className="w-[37.13px] h-[37.13px]"
                    />
                    <p className="text-[#253FF6] text-xl font-bold">
                      {t("quest.taskQuest.rallyCoins")}
                    </p>
                  </div>
                  <div className="flex flex-col justify-end items-end">
                    <p className="text-[#253FF6] text-5xl font-bold">
                      {parseInt(availableToken)}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex flex-col ">
                <p className="text-[#333333] font-bold text-lg">
                  {t("withDraw.enterCoinsToWithdraw")}
                </p>
                <CustomInputProps
                  value={tempWithdrawAmount}
                  onChange={handleWithdrawAmountChange}
                  iconImage={images.RallyCoinSmallBlueIcon}
                  isCurrencyInput={true}
                  classNameInput={"text-[#253FF6] font-bold text-2xl"}
                  error={error.withdrawAmount}
                />
                {error.withdrawAmount && (
                  <p className="text-xs text-[#EF4056] mt-1">
                    {error.withdrawAmount}
                  </p>
                )}

                <div>
                  <p className="text-[#A0A0A0] text-sm  font-medium">
                    - {t("withDraw.withdrawalFee")}: $
                    {calculateReceiveAmount?.data?.transaction_fee > 0
                      ? calculateReceiveAmount?.data?.transaction_fee
                      : 0.5}
                  </p>
                </div>
                <div className="flex items-center justify-start gap-1.5">
                  <p className="text-[#333333] font-bold text-lg align-middle">
                    =
                  </p>
                  <img src={images.FlagImage} alt="" className="w-6 h-6" />
                  <span
                    className={` ${
                      calculateReceiveAmount?.data?.receive_amount &&
                      error.withdrawAmount === ""
                        ? "text-[#333333]"
                        : "text-[#E9E6E4]"
                    } font-bold text-2xl`}
                  >
                    $
                    {calculateReceiveAmount?.data?.receive_amount &&
                    error.withdrawAmount === ""
                      ? calculateReceiveAmount?.data?.transaction_fee > 0
                        ? calculateReceiveAmount?.data?.receive_amount.toFixed(
                            2
                          )
                        : (
                            calculateReceiveAmount?.data?.receive_amount - 0.5
                          ).toFixed(2)
                      : "0.00"}
                  </span>
                </div>
                <div className="border-l border-[#E9E6E4] w-5 h-5 pl-2"></div>
                <p className="text-[#333333] font-bold text-lg">
                  {t("withDraw.enterPaynowAccount")}
                </p>
                <CustomInputProps
                  value={rawBankAccount || ""}
                  onChange={handleBankAccountChange}
                  countryCode="+65"
                  iconImage={images.PayNow}
                  placeholder={"0000-0000"}
                  error={error.bankAccount}
                />
                {error.bankAccount && (
                  <p className="text-xs text-[#EF4056] mt-1">
                    {error.bankAccount}
                  </p>
                )}
              </div>
              <Button
                text={t("withDraw.withdraw")}
                className="bg-[#253FF6] text-white font-medium text-xl rounded-md h-[50px] py-3 px-[18px]"
                onClick={openWithdrawModal}
                disabled={
                  bankAccount === "" ||
                  withdrawAmount === "" ||
                  error.withdrawAmount !== "" ||
                  error.bankAccount !== "" ||
                  isWithdrawing ||
                  isLoadingWithdrawDetails
                }
              />
              <div>
                <p
                  onClick={() => navigate("/profile/history")}
                  className="text-[#A0A0A0] text-base font-medium text-center underline cursor-pointer"
                >
                  {t("historyWithdraw.withdrawHistory")}
                </p>
              </div>
            </div>
            <CommonModal
              ref={modalRef}
              title={t("withDraw.confirmWithdraw")}
              classNameText={"text-[#253FF6] font-medium text-2xl"}
              actions={(closeModal) => (
                <>
                  <Button
                    text={t("base.confirm")}
                    className="flex-1 px-6 py-1.5 rounded-xl min-h-[40px] text-white text-xl font-medium bg-[#253FF6]"
                    variant="primary"
                    // onClick={handlePauseTakeQuest}
                    onClick={handleConfirmWithdraw}
                    disabled={
                      bankAccount === "" ||
                      withdrawAmount === "" ||
                      isWithdrawing ||
                      isLoadingWithdrawDetails
                    }
                  />
                  <Button
                    text={t("base.cancel")}
                    className="flex-1 px-6 py-1.5 text-xl font-medium rounded-xl min-h-[40px] bg-white border border-[#253FF6] text-[#253FF6]"
                    onClick={() => closeModal(false)}
                  />
                </>
              )}
            >
              <div className="flex flex-col gap-3 mt-4">
                <div className="flex flex-col items-center justify-center">
                  <img src={images.RallyCoins} alt="" className="w-6 h-6" />
                  <p className="text-[#253FF6] font-bold text-2xl">
                    {withdrawAmount}
                  </p>
                </div>
                <div className=" text-[#E9E6E4] flex items-center justify-center">
                  <span className=" text-[15px]">|</span>
                </div>
                <div className="flex flex-col items-center justify-center">
                  <img src={images.FlagImage} alt="" className="w-6 h-6" />
                  <p className="text-[#333333] font-bold text-2xl">
                    {/* ${calculateReceiveAmount?.data?.receive_amount} */}$
                    {calculateReceiveAmount?.data?.receive_amount &&
                    error.withdrawAmount === ""
                      ? calculateReceiveAmount?.data?.transaction_fee > 0
                        ? calculateReceiveAmount?.data?.receive_amount.toFixed(
                            2
                          )
                        : (
                            calculateReceiveAmount?.data?.receive_amount - 0.5
                          ).toFixed(2)
                      : 0.0}
                  </p>
                </div>
                <div className=" text-[#E9E6E4] flex items-center justify-center">
                  <span className=" text-[15px]">|</span>
                </div>
                <div className="flex flex-col items-center justify-center">
                  <img src={images.PayNow} alt="" className="w-6 h-6" />
                  <p className="text-[#333333] font-bold text-2xl">{`+65 ${rawBankAccount}`}</p>
                </div>
              </div>
            </CommonModal>
          </div>
        </>
      )}
    </>
  );
};

export default WithDraw;
